

import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { MODE, MODE_KEY_TO_ID } from "../../common/ENUMS/VENTILATOR_MODES_ENUM";
import './index.scss';
import { setVentilatorWaveformData } from "../../socket/socket";
import { Line } from "react-chartjs-2";
import { PATIENT_TYPE, VENTILATOR_ADDITIONAL_FEATURES } from "../../common/ENUMS/PARAMETER_ENUM";
import SnakeCanvas from "../Chart/SnakeCanvas";
import DataQueue from "../../common/utils/DataQueue";
import VentilatorChartDesktopView from "./VentilatorChartDesktopView";
import VentilatorChartMobileView from "./VentilatorChartMobileView";

import { USER_VIEW } from "../../common/constant/common-constants";

const EMPTY_VENTILATOR_ADDITIONAL_FEATURES = [];
const EMPTY_VENTILATOR_MODULE_CONNECTION_STATUS = {};

const dataRange = {
  current: {
    pressure: { min: 0, max: 70 },
    flow: { min: -30, max: 30 },
    volume: { min: 0, max: 200 },
    spo2: { min: -100, max: 100 },
    co2: { min: 0, max: 25 },
    tpp: { min: -25, max: 25 },
  }
};


const VentilatorChart = (props) => {
  const [bedId, setBedId] = useState(null);
  const [tppEnabled, setTppEnabled] = useState(false);

  const pressureQueue = useRef(new DataQueue());
  const flowQueue = useRef(new DataQueue());
  const volumeQueue = useRef(new DataQueue());
  const spo2Queue = useRef(new DataQueue());
  const co2Queue = useRef(new DataQueue());
  const tppQueue = useRef(new DataQueue());
  const co2_points_lag = useRef(0);
  const new_co2_points_lag = useRef(0);
  const modeRef = useRef();




  const canvasSize = useRef({ width: 1000, height: 150 });
  const graphDrawerRef = useRef({
    pressure: null,
    flow: null,
    volume: null,
    spo2: null,
    co2: null,
    tpp: null,
  });


  // console.log("flowQueue.current", flowQueue.current)

  const waveSocket = (event) => {
    if (event.bedId !== props.bedId || !event.observedAt) return;
    const { observedAt, waveformdata } = event;


    pressureQueue.current.push(
      waveformdata?.pressure?.map((v, index) => ({ value: v, color: waveformdata?.trigger?.[index] == 0 ? "#3ADAE5" : "#FF0000" }))
    );

    flowQueue.current.push(
      waveformdata?.flow?.map((v, index) => ({ value: v, color: waveformdata?.trigger?.[index] == 0 ? "#FCC331" : "#FF0000" }))
    );

    volumeQueue.current.push(
      waveformdata?.volume?.map(v => ({ value: v, color: "#299842" }))
    );

    co2Queue.current.push(
      waveformdata?.co2?.map(v => ({ value: v, color: "#5247E7" }))
    );

    spo2Queue.current.push(
      waveformdata?.spo2?.map(v => ({ value: v, color: "#EF9A46" }))
    );

    tppQueue.current.push(
      waveformdata?.tpp?.map(v => ({ value: v, color: "#fadd28" }))
    );

    const nextTppEnabled = !!waveformdata?.tpp_enabled;
    setTppEnabled(prev => (prev !== nextTppEnabled ? nextTppEnabled : prev));

    new_co2_points_lag.current = waveformdata?.co2_points_lag || 0;
  };

  useEffect(() => {
    setVentilatorWaveformData(waveSocket);

    const draw = () => {
      // consume 25 points every 50ms is ideal case (500 points per second)
      // if the queue has more data then speedup consumption to avoid lag
      let pointsToConsume = 25;

      if (pressureQueue.current.size() > 5000) {
        // clear all to avoid huge lag
        pressureQueue.current.clear();
        flowQueue.current.clear();
        volumeQueue.current.clear();
        co2Queue.current.clear();
        spo2Queue.current.clear();
        tppQueue.current.clear();
      }
      else if (pressureQueue.current.size() > 500) {
        pointsToConsume = 100;
      } else if (pressureQueue.current.size() >= 400) {
        pointsToConsume = 35;
      }
      else if (flowQueue.current.size() >= 350) {
        pointsToConsume = 30;
      }
      else if (flowQueue.current.size() >= 300) {
        pointsToConsume = 26;
      }
      else {
        pointsToConsume = 25;
      }

      let tppPointsToConsume = 50;
      if (tppQueue.current.size() > 1000) {
        tppPointsToConsume = 200;
      }
      else if (tppQueue.current.size() >= 800) {
        tppPointsToConsume = 70;
      }
      else if (tppQueue.current.size() >= 700) {
        tppPointsToConsume = 60;
      }
      else if (tppQueue.current.size() >= 600) {
        tppPointsToConsume = 52;
      }

      let spo2PointsToConsume = 13;
      if (spo2Queue.current.size() > 500) {
        spo2PointsToConsume = 50;
      }
      else if (spo2Queue.current.size() > 300) {
        spo2PointsToConsume = 25;
      }
      else if (spo2Queue.current.size() >= 200) {
        spo2PointsToConsume = 20;
      }
      else if (spo2Queue.current.size() >= 150) {
        spo2PointsToConsume = 15;
      }


      let newPressurePoints = pressureQueue.current.popN(pointsToConsume);
      let newFlowPoints = flowQueue.current.popN(pointsToConsume);
      let newVolumePoints = volumeQueue.current.popN(pointsToConsume);
      let newSpo2Points = spo2Queue.current.popN(spo2PointsToConsume);
      let newTppPoints = tppQueue.current.popN(tppPointsToConsume);
      //  console.log("new flow points", newFlowPoints);

      let co2PointsToPop = 0;
      if (co2Queue.current.size() > pressureQueue.current.size() + co2_points_lag.current) {
        co2PointsToPop = co2Queue.current.size() - (pressureQueue.current.size() + co2_points_lag.current);
      }

      let newCo2Points = co2Queue.current.popN(co2PointsToPop);

      if (pointsToConsume == co2PointsToPop) {

        // const modeId = props.ventilatorPatientModeAlarmProfileData?.modeId || null;
        const modeId = modeRef.current;

        // console.log("mode inside draw:", modeId);

        if (modeId === MODE_KEY_TO_ID["HFOT"]) {
          if (graphDrawerRef.current.flow) {
            graphDrawerRef.current.flow(newFlowPoints);
            graphDrawerRef.current.spo2?.(newSpo2Points);
          }

        }
        else
          if (graphDrawerRef.current.pressure &&
            graphDrawerRef.current.flow &&
            graphDrawerRef.current.volume) {

            graphDrawerRef.current.pressure(newPressurePoints);
            graphDrawerRef.current.flow(newFlowPoints);
            graphDrawerRef.current.volume(newVolumePoints);
            graphDrawerRef.current.co2?.(newCo2Points);
            graphDrawerRef.current.spo2?.(newSpo2Points);
            graphDrawerRef.current.tpp?.(newTppPoints);
          }
      }

    };

    const interval = setInterval(draw, 50);

    return () => {
      clearInterval(interval);
    };

  }, []);

  useEffect(() => {
    if (bedId !== props.bedId) {
      setVentilatorWaveformData(waveSocket);
      setBedId(props.bedId);

      pressureQueue.current.clear();
      flowQueue.current.clear();
      volumeQueue.current.clear();
      spo2Queue.current.clear();
      co2Queue.current.clear();
      tppQueue.current.clear();
    }
  }, [props.bedId]);

  useEffect(() => {
    modeRef.current = props.ventilatorPatientModeAlarmProfileData?.modeId;
  }, [props.ventilatorPatientModeAlarmProfileData?.modeId]);




  return (
    <>
      {props.config.userView === USER_VIEW.DESKTOP && (
        <VentilatorChartDesktopView
          ventilatorPatientModeAlarmProfileData={props.ventilatorPatientModeAlarmProfileData}
          ventilatorAdditionalFeaturesData={props.ventilatorAdditionalFeaturesData}
          ventilatorModuleConnectionStatus={props.ventilatorModuleConnectionStatus}
          tppEnabled={tppEnabled}
          dataRange={dataRange}
          graphDrawerRef={graphDrawerRef}
          canvasSize={canvasSize}
        />
      )}

      {props.config.userView === USER_VIEW.MOBILE && (
        <VentilatorChartMobileView
          ventilatorPatientModeAlarmProfileData={props.ventilatorPatientModeAlarmProfileData}
          ventilatorAdditionalFeaturesData={props.ventilatorAdditionalFeaturesData}
          ventilatorModuleConnectionStatus={props.ventilatorModuleConnectionStatus}
          tppEnabled={tppEnabled}
          dataRange={dataRange}
          graphDrawerRef={graphDrawerRef}
          canvasSize={canvasSize}
        />
      )}
    </>
  );

};

const mapStateToProps = (state, ownProps) => {
  let ventilatorPatientModeAlarmProfileData = state?.deviceVitalDataReducer?.ventilatorPatientModeAlarmProfileData?.[ownProps.bedId];
  let ventilatorAdditionalFeaturesData =
    state.deviceVitalDataReducer?.ventilatorHeartbeat?.[ownProps.bedId]
      ? (state?.deviceVitalDataReducer?.ventilatorAdditionalFeaturesData?.[ownProps.bedId]?.ventilatorAdditionalFeaturesData
        ?? EMPTY_VENTILATOR_ADDITIONAL_FEATURES)
      : EMPTY_VENTILATOR_ADDITIONAL_FEATURES;
  let ventilatorModuleConnectionStatus =
    state.deviceVitalDataReducer?.ventilatorHeartbeat?.[ownProps.bedId]
      ? (state?.deviceVitalDataReducer?.ventilatorModuleConnectionStatus?.[ownProps.bedId]?.ventilatorModuleConnectionStatus
        ?? EMPTY_VENTILATOR_MODULE_CONNECTION_STATUS)
      : EMPTY_VENTILATOR_MODULE_CONNECTION_STATUS;

  return {
    ventilatorPatientModeAlarmProfileData: ventilatorPatientModeAlarmProfileData,
    ventilatorAdditionalFeaturesData: ventilatorAdditionalFeaturesData,
    ventilatorModuleConnectionStatus: ventilatorModuleConnectionStatus,
    config: state.configReducer.config
  };
};

export default connect(mapStateToProps, null)(VentilatorChart);
